import { STATIC_ICONS } from 'virtual:static-icons'
import { ImageOff } from 'lucide-react'
import { type ReactNode, useId, useMemo, useState } from 'react'
import {
  AuthoredIcon,
  isAuthoredEmoji,
} from '@/components/admin/shared/authored-icon'
import { ICON_EMOJI } from '@/components/admin/shared/icon-emoji'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

const ICON_TABS = ['emoji', 'bundled', 'url'] as const
type IconTab = (typeof ICON_TABS)[number]

const ICON_TAB_LABELS: Record<IconTab, string> = {
  emoji: 'Emoji',
  bundled: 'Bundled',
  url: 'URL',
}

function isIconTab(value: string): value is IconTab {
  return (ICON_TABS as readonly string[]).includes(value)
}

/**
 * Where to open the field for an existing value, so editing an icon lands the
 * author on the tab that produced it. An empty value opens on Emoji — the
 * quickest way to get any icon at all.
 */
function initialTab(value: string): IconTab {
  const trimmed = value.trim()
  if (trimmed === '') return 'emoji'
  if (isAuthoredEmoji(trimmed)) return 'emoji'
  if (STATIC_ICONS.includes(trimmed)) return 'bundled'
  return 'url'
}

const GRID_CLASS =
  'grid max-h-40 grid-cols-8 gap-1 overflow-y-auto rounded-md border border-input p-2'

const CELL_CLASS =
  'flex h-8 w-8 items-center justify-center rounded-md text-lg hover:bg-muted aria-pressed:bg-muted aria-pressed:ring-1 aria-pressed:ring-ring'

interface IconFieldProps {
  /** The field's visible caption, also used as the group's accessible name. */
  label: ReactNode
  value: string
  onChange: (value: string) => void
}

/**
 * One control for every icon format the `icon` column accepts. All three tabs
 * write the same single string — an emoji, a bundled filename, or a URL — so
 * the stored shape is unchanged and hand-typed values keep working.
 *
 * Uploading an image is deliberately absent: there is no blob storage in this
 * app, and `static/icons/` is committed to git, so "add an image" means
 * committing the file and picking it from the Bundled tab.
 */
export function IconField({
  label,
  value,
  onChange,
}: IconFieldProps): ReactNode {
  const reactId = useId()
  const labelId = `${reactId}-label`
  const urlInputId = `${reactId}-url`

  const [tab, setTab] = useState<IconTab>(() => initialTab(value))
  const [lastValue, setLastValue] = useState(value)
  const [filter, setFilter] = useState('')

  // The callers reuse one IconField instance across records rather than
  // remounting it, so a value that changed from outside has to re-open the
  // tab that produced it, and the Bundled tab's filter has to clear too —
  // otherwise a search string left over from the previous record can hide
  // the new record's icon. Edits made through this field record their own
  // value in `emit` below, so typing a bundled filename into the URL box
  // does not yank the author onto the Bundled tab mid-keystroke.
  if (value !== lastValue) {
    setLastValue(value)
    setTab(initialTab(value))
    setFilter('')
  }

  function emit(next: string) {
    setLastValue(next)
    onChange(next)
  }

  const bundled = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    if (needle === '') return STATIC_ICONS
    return STATIC_ICONS.filter((name) => name.toLowerCase().includes(needle))
  }, [filter])

  return (
    <div role="group" aria-labelledby={labelId} className="space-y-2">
      <span
        id={labelId}
        className="text-muted-foreground mb-1 block text-sm font-medium"
      >
        {label}
      </span>

      <div className="flex items-center gap-2">
        <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded text-xl">
          <AuthoredIcon
            icon={value}
            className="text-muted-foreground"
            fallback={ImageOff}
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Pick an emoji, a bundled image, or paste a URL. Leave empty for the
          default icon.
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(next) => {
          if (isIconTab(next)) setTab(next)
        }}
      >
        <TabsList>
          {ICON_TABS.map((t) => (
            <TabsTrigger key={t} value={t}>
              {ICON_TAB_LABELS[t]}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="emoji">
          {tab === 'emoji' && (
            <div className={GRID_CLASS}>
              {ICON_EMOJI.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  aria-pressed={value === emoji}
                  onClick={() => emit(emoji)}
                  className={CELL_CLASS}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bundled">
          <div className="space-y-2">
            <Input
              type="text"
              aria-label="Filter bundled icons"
              placeholder="Filter icons"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            {tab === 'bundled' &&
              (bundled.length === 0 ? (
                <p className="text-muted-foreground px-1 py-2 text-xs">
                  No bundled icon matches “{filter}”.
                </p>
              ) : (
                <div className={GRID_CLASS}>
                  {bundled.map((name) => (
                    <button
                      key={name}
                      type="button"
                      aria-label={name}
                      aria-pressed={value === name}
                      onClick={() => emit(name)}
                      className={CELL_CLASS}
                    >
                      <AuthoredIcon
                        icon={name}
                        className="text-muted-foreground"
                        fallback={ImageOff}
                      />
                    </button>
                  ))}
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="url">
          {/*
           * The group's caption (`labelId`) names the whole group, not this
           * input specifically — it's hidden via TabsContent's `hidden`
           * attribute whenever another tab is active, so a <label htmlFor>
           * pointed at it would be a dead click target most of the time.
           * This input keeps its own aria-label because, unlike the group,
           * it specifically holds a URL or filename.
           */}
          <Input
            id={urlInputId}
            type="text"
            aria-label="Icon URL or filename"
            value={value}
            onChange={(e) => emit(e.target.value)}
            placeholder="https://example.com/icon.png or leetcode.webp"
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
